/**
 * @file This file constains a state machine library.
 */
import { assert } from '@codegrade/utils';
const DEFAULT_TRANSITION_OPTS = Object.freeze({
    mutateDataOk: true,
});
const DEFAULT_STATE_OPTS = Object.freeze({
    frozen: true,
});
/**
 * Create a new state with a minimal amount of copies.
 *
 * @param name The name of the state.
 * @param data The data of the state.
 * @param opts The state options.
 * @param transitionOpts The transition options.
 * @returns A new state object that is frozen if possible.
 */
function makeState(name, data, opts, transitionOpts) {
    if (opts.frozen) {
        if (transitionOpts?.mutateDataOk ?? DEFAULT_TRANSITION_OPTS.mutateDataOk) {
            return Object.freeze({
                name,
                data: Object.freeze(data),
            });
        }
        else {
            return Object.freeze({ name, data: Object.freeze({ ...data }) });
        }
    }
    else {
        return { name, data };
    }
}
/**
 * A class to represent a token that can be used to transition state
 * machines.
 */
export class TransitionToken {
    /**
     * @param v The value of the token.
     */
    constructor(v) {
        this.v = v;
        Object.freeze(this);
    }
    /**
     * Create a new token.
     *
     * @returns A new and unique `TransitionToken`.
     */
    static create() {
        return new TransitionToken(TransitionToken.tokenNr++);
    }
    /**
     * Increment this token to the next one.
     *
     * This is the same as {@link TransitionToken.create}.
     *
     * @returns A new and unique `TransitionToken`.
     */
    next() {
        return TransitionToken.create();
    }
    /**
     * Check if two tokens are equal.
     *
     * @param other The other `TransitionToken`.
     * @returns A boolean indicating if the two tokens are equal.
     */
    eq(other) {
        return this.v === other.v;
    }
}
/** The last tokenNr that was given out. */
TransitionToken.tokenNr = 0;
/**
 * A currently running state machine.
 *
 * This instance is mutable, and should not be frozen.
 */
export class RunningStateMachine {
    /**
     * @param opts The options for this running state machine.
     * @param current The current state.
     * @param onReset Function called one tick after the state machine was
     * initialized or reset.
     */
    constructor(opts, current, onReset) {
        this.opts = opts;
        this.current = current;
        /** The current token that can be used to do transitions. */
        this.curToken = TransitionToken.create();
        /** Function to be called when the state machine is reset. */
        this.onReset = () => { };
        this.initial = current;
        if (onReset != null) {
            /** Called when the state machine is reset. */
            this.onReset = () => {
                void Promise.resolve().then(() => onReset(this.curToken));
            };
            this.onReset();
        }
    }
    /**
     * Get the current state.
     *
     * @returns The current state.
     */
    get cur() {
        return this.current;
    }
    /**
     * Match over all the possible states.
     *
     * @param matchers The matchers to use. This is an object where
     *     the keys are the states and the values are {@link
     *     MatcherFunction}s.
     * @returns The return value that was returned by the matcher for
     *     the current state.
     */
    match(matchers) {
        const matcher = matchers[this.cur.name];
        if (matcher == null) {
            assert.AssertionError.assertNever(matcher, `No matcher for current state "${String(this.cur.name).toLocaleString()}"`);
        }
        return matcher(this.cur.data, { token: this.curToken });
    }
    /**
     * Get the current valid transition token.
     *
     * @returns A transition token that can be used, at this moment.
     */
    getTokenForForceTransition() {
        return this.curToken;
    }
    /**
     * Same as {@link RunningStateMachine#transitionTo} only it always
     * transitions.
     *
     * @param name The name of the new state.
     * @param data The data of the new state.
     * @param transitionOpts The transition options to use.
     * @returns A transition token that can be used to do a non-forced
     *     transition.
     */
    forceTransitionTo(name, data, transitionOpts) {
        this.curToken = this.curToken.next();
        this.current = makeState(name, data, this.opts[name], transitionOpts);
        return this.curToken;
    }
    /**
     * Transition the state machine to a new state.
     *
     * @param token The token to used for transitioning. If this is no
     *     longer the current token this method does nothing.
     * @param name The name of the new state.
     * @param data The data of the new state.
     * @param transitionOpts Options to use for transitioning.
     * @returns A token that can be used to transition to a new state
     *     again. Such a token can only be used once to transition.
     */
    transitionTo(token, name, data, transitionOpts) {
        if (token.eq(this.curToken)) {
            return this.forceTransitionTo(name, data, transitionOpts);
        }
        else {
            // Don't return `curToken` in this case as the returned
            // token shouldn't be valid.
            return token;
        }
    }
    /** Reset the state machine back to its initial state. */
    reset() {
        this.current = this.initial;
        this.curToken = this.curToken.next();
        this.onReset();
    }
}
/** A class to define to set a definition of a state machine. */
export class Definition {
    /**
     * @param opts The options for this definition.
     */
    constructor(opts) {
        this.opts = opts;
        Object.freeze(this);
    }
    /**
     * Create a new definition.
     *
     * @returns A new definition without any states.
     * @see {@link Definition#addState} to add states.
     */
    static create() {
        return new Definition({});
    }
    /**
     * Add a state to the definition.
     *
     * @param name The name of the state. If not unique in the
     *     Definition this function will return the `never` type.
     * @param opts Options to be used for this state.
     * @returns A new definition that can be used.
     * @example Adding a new state
     * Definition.create()
     *     .addState<'my-state-name', { type: 'my-state-data' }>('my-state-name')
     */
    addState(name, opts) {
        if (this.opts[name] != null) {
            return assert.AssertionError.assert(false, `The state ${name} already existed`);
        }
        const newOpts = {
            ...this.opts,
            [name]: opts == null
                ? DEFAULT_STATE_OPTS
                : Object.freeze({ ...DEFAULT_STATE_OPTS, ...opts }),
        };
        const res = new Definition(Object.freeze(newOpts));
        return res;
    }
    /**
     * Start running this definition.
     *
     * @param initial The name of the initial state.
     * @param data The data for the initial state.
     * @param onReset Function to be called one tick after the state machine is
     * initialized or reset.
     * @param transitionOpts Options for transition to this state.
     * @returns A running state machine that is in the given `initial` state.
     */
    run(initial, data, onReset, transitionOpts) {
        const state = makeState(initial, data, this.opts[initial], transitionOpts);
        return new RunningStateMachine(this.opts, state, onReset);
    }
}
